import React, { useMemo, useState } from "react";

import { Button, Card, InputField, Text, View } from "../components/base";
import { Form } from "../components/base/Form";
import { sessionActions } from "../redux/reducers/sessionSlice";
import { useAppDispatch, useAppSelector } from "../redux";
import { useNavigation } from "../hooks/useNavigation";
import { useSearchParams } from "react-router-dom";
import { makeRequest } from "../utils/request";

type FormData = {
  email: string;
};

export const ForgotPasswordPage = () => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.session.isLoading);
  const [formData, setFormData] = useState<FormData>({ email: "" });
  const [resetPasswordSent, setResetPasswordSent] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [newPassword, setNewPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const onChangeFormData = (newFormData: Record<string, unknown>) => {
    setFormData(newFormData as FormData);
  };

  const onClickContinue = async () => {
    if (token) {
      try {
        setErrorMessage("");
        await makeRequest("/v1/auth/reset/confirm", {
          method: "POST",
          body: { token, password: newPassword },
        });
        navigation.navigate("/");
      } catch (error: any) {
        const message = error?.error || error?.message || "Invalid or expired reset link. Please request a new one.";
        setErrorMessage(message);
      }
      return;
    }

    if (resetPasswordSent) {
      navigation.goBack();
    } else {
      await dispatch(sessionActions.resetPassword(formData));
      setResetPasswordSent(true);
    }
  };

  return (
    <View className="w-full min-h-screen flex items-center justify-center p-4">
      <View className="w-full max-w-md space-y-8">
        {/* Header */}
        <View className="flex flex-col items-center text-center space-y-2">
          <Text className="text-4xl font-bold">Reset Password</Text>
          <Text
            variant="muted"
            className="text-lg"
          >
            {token ? "Enter a new password" : "Enter your email to reset your password"}
          </Text>
        </View>

        {/* Reset Form */}
        <Card className="p-6 space-y-6">
          <Button
            variant="ghost"
            type="button"
            onClick={() => navigation.goBack()}
            className="!p-0 text-brand !justify-start dark:text-brand-light hover:opacity-80"
          >
            ← Back to Sign In
          </Button>

          {token ? (
            <Form
              data={{ password: newPassword }}
              onChange={(data) => setNewPassword((data as any).password)}
            >
              <InputField
                label="New Password"
                name="password"
                placeholder="Enter new password"
                type="password"
              />
            </Form>
          ) : resetPasswordSent ? (
            <View>
              <Text variant="muted">We've sent you a link to reset your password if an account exists with that email address.</Text>
            </View>
          ) : (
            <Form
              data={formData}
              onChange={onChangeFormData}
            >
              <InputField
                label="Email Address"
                name="email"
                placeholder="Enter your email"
              />
            </Form>
          )}

          {token && errorMessage && (
            <Text
              variant="muted"
              className="text-red-600 dark:text-red-400"
            >
              {errorMessage}{" "}
              <button
                className="underline ml-1"
                onClick={() => navigation.navigate("/forgot-password")}
              >
                Request new link
              </button>
            </Text>
          )}

          <Button
            onClick={onClickContinue}
            isDisabled={token ? !newPassword : !formData.email}
            isLoading={isLoading}
          >
            {token ? "Set New Password" : resetPasswordSent ? "Back to Sign In" : "Send Reset Link"}
          </Button>
        </Card>
      </View>
    </View>
  );
};
